import React, { useMemo } from 'react';
import { Select, InputNumber } from 'antd';
import { useLayerProperitesContext, ColorRamp } from '../../../../context/LayerPropertiesContext';
import ColorRampSelect from './components/ColorRampSelect';
import { HeatmapSymbologyConfig, RadiusUnit } from '../../../../types/symbology.types';
import { defaultHeatmapConfig, RADIUS_UNITS, ZOOM_OPTIONS, isValidOpacity, OPACITY_ERROR, ZOOM_ERROR } from './heatmap.utils';
import '../LayerProperties.scss';

const HeatmapSymbology: React.FC = () => {
  const { symbologyConfig, setState } = useLayerProperitesContext();

  const config: HeatmapSymbologyConfig = useMemo(() => {
    const base = symbologyConfig && symbologyConfig.type === 'heatmap' ? symbologyConfig : defaultHeatmapConfig();
    // Layers saved before the >= 1 rule can hold 0, which would render and re-save as-is.
    return base.groupRadius >= 1 ? base : { ...base, groupRadius: 1 };
  }, [symbologyConfig]);

  const commit = (patch: Partial<HeatmapSymbologyConfig>) => {
    setState((prev) => ({ ...prev, symbology: 'heatmap', symbologyConfig: { ...config, ...patch } }));
  };

  const opacityInvalid = !isValidOpacity(config.opacity);
  const zoomInvalid = config.minZoom >= config.maxZoom; // Scenario 9

  return (
    <div className="lp-symbology__stack">
      <div className="lp-symbology__card">
        {/* COLOUR RAMP + OPACITY (US-101677 Scenarios 3-5) */}
        <div className="lp-heatmap__row">
          <span className="lp-heatmap__label">COLOUR RAMP</span>
          <ColorRampSelect value={config.colorRamp} onChange={(ramp: ColorRamp) => commit({ colorRamp: ramp })} bare />
          <InputNumber
            className="lp-heatmap__opacity"
            value={config.opacity}
            min={0}
            max={100}
            precision={0}
            status={opacityInvalid ? 'error' : undefined}
            onChange={(v) => commit({ opacity: typeof v === 'number' ? v : config.opacity })}
            formatter={(v) => `${v}%`}
            parser={(v) => (v ? Number(String(v).replace('%', '')) : 0) as 0}
          />
        </div>
        {opacityInvalid && <div className="lp-unique__display-error">{OPACITY_ERROR}</div>}

        {/* GROUP RADIUS + UNIT (Scenarios 6-7) */}
        <div className="lp-heatmap__row">
          <span className="lp-heatmap__label">GROUP RADIUS</span>
          <InputNumber
            className="lp-heatmap__radius"
            value={config.groupRadius}
            min={1}
            precision={0}
            onChange={(v) => commit({ groupRadius: typeof v === 'number' ? Math.max(1, Math.trunc(v)) : Math.max(1, config.groupRadius) })}
            placeholder="Enter value"
          />
          <Select
            className="lp-heatmap__unit"
            value={config.radiusUnit}
            onChange={(unit: RadiusUnit) => commit({ radiusUnit: unit })}
            options={RADIUS_UNITS}
          />
        </div>

        {/* MIN / MAX ZOOM (Scenarios 8-9) */}
        <div className="lp-heatmap__row">
          <span className="lp-heatmap__label">MIN ZOOM</span>
          <Select
            className="lp-heatmap__zoom"
            value={config.minZoom}
            onChange={(z: number) => commit({ minZoom: z })}
            options={ZOOM_OPTIONS.map((z) => ({ value: z, label: String(z) }))}
          />
          <span className="lp-heatmap__label">MAX ZOOM</span>
          <Select
            className="lp-heatmap__zoom"
            value={config.maxZoom}
            onChange={(z: number) => commit({ maxZoom: z })}
            options={ZOOM_OPTIONS.map((z) => ({ value: z, label: String(z) }))}
          />
        </div>
        {zoomInvalid && <div className="lp-unique__display-error">{ZOOM_ERROR}</div>}
      </div>
    </div>
  );
};

export default HeatmapSymbology;
